import React from 'react';
import { motion } from 'framer-motion';
import { Check, Shield, Share2, Hexagon, ArrowRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';

const LogoConcepts = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Logo Concepts</h1>
          <p className="text-xl text-gray-600">Based on the "Competence" & "Trust" Brand Identity</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          
          {/* Option 1: Corporate Authority */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="h-full border-t-8 border-t-blue-900 hover:shadow-2xl transition-all duration-300">
              <CardHeader className="text-center pb-2">
                <div className="mx-auto mb-6 bg-white p-8 rounded-xl shadow-inner">
                  {/* Logo SVG */}
                  <svg width="200" height="80" viewBox="0 0 200 80" className="mx-auto">
                    {/* Icon: Hexagon T */}
                    <path d="M30 10 L55 25 L55 55 L30 70 L5 55 L5 25 Z" fill="#1e3a8a" />
                    <path d="M20 30 L40 30 L40 60 L20 60 L20 45 L30 45 L30 35 L20 35 Z" fill="white" />
                    {/* Text */}
                    <text x="70" y="55" fontFamily="Inter, sans-serif" fontWeight="800" fontSize="38" fill="#1e3a8a">TEQVIS</text>
                    <circle cx="190" cy="55" r="4" fill="#3b82f6" />
                  </svg>
                </div>
                <CardTitle className="text-2xl font-bold text-gray-900">1. Corporate Authority</CardTitle>
                <CardDescription>Stable, Established, Expert</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-center space-x-2 text-sm">
                  <div className="flex items-center space-x-1">
                    <div className="w-4 h-4 rounded-full bg-[#1e3a8a]"></div>
                    <span>Deep Navy</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <div className="w-4 h-4 rounded-full bg-[#3b82f6]"></div>
                    <span>Royal Blue</span>
                  </div>
                </div>
                <p className="text-gray-600 text-sm text-center">
                  Uses a hexagonal shield shape to represent strength and structure. The bold, heavy typography signals an established enterprise.
                </p>
              </CardContent>
            </Card>
          </motion.div>

          {/* Option 2: Future Vision */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card className="h-full border-t-8 border-t-indigo-700 hover:shadow-2xl transition-all duration-300">
              <CardHeader className="text-center pb-2">
                <div className="mx-auto mb-6 bg-white p-8 rounded-xl shadow-inner">
                  {/* Logo SVG */}
                  <svg width="200" height="80" viewBox="0 0 200 80" className="mx-auto">
                    {/* Icon: Tech Nodes */}
                    <circle cx="30" cy="20" r="5" fill="#4338ca" />
                    <circle cx="10" cy="50" r="5" fill="#4338ca" />
                    <circle cx="50" cy="50" r="5" fill="#4338ca" />
                    <line x1="30" y1="20" x2="10" y2="50" stroke="#4338ca" strokeWidth="3" />
                    <line x1="30" y1="20" x2="50" y2="50" stroke="#4338ca" strokeWidth="3" />
                    <line x1="10" y1="50" x2="50" y2="50" stroke="#4338ca" strokeWidth="3" />
                    {/* Text */}
                    <text x="70" y="55" fontFamily="Inter, sans-serif" fontWeight="300" fontSize="42" letterSpacing="2" fill="#1e1b4b">teqvis</text>
                  </svg>
                </div>
                <CardTitle className="text-2xl font-bold text-gray-900">2. Future Vision</CardTitle>
                <CardDescription>Modern, Connected, Innovative</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-center space-x-2 text-sm">
                  <div className="flex items-center space-x-1">
                    <div className="w-4 h-4 rounded-full bg-[#4338ca]"></div>
                    <span>Indigo</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <div className="w-4 h-4 rounded-full bg-gray-400"></div>
                    <span>Silver</span>
                  </div>
                </div>
                <p className="text-gray-600 text-sm text-center">
                  Features connected nodes representing a network/cloud data. Lowercase, thin typography feels approachable and high-tech.
                </p>
              </CardContent>
            </Card>
          </motion.div>

          {/* Option 3: Tech Reliability */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card className="h-full border-t-8 border-t-blue-600 hover:shadow-2xl transition-all duration-300">
              <CardHeader className="text-center pb-2">
                <div className="mx-auto mb-6 bg-white p-8 rounded-xl shadow-inner">
                  {/* Logo SVG */}
                  <svg width="200" height="80" viewBox="0 0 200 80" className="mx-auto">
                    {/* Icon: Check Shield */}
                    <path d="M30 15 C30 15, 55 15, 55 15 C55 15, 55 40, 30 65 C 5 40, 5 15, 5 15 Z" fill="#2563eb" />
                    <path d="M20 35 L28 43 L42 27" stroke="white" strokeWidth="4" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                    {/* Text */}
                    <text x="70" y="55" fontFamily="Inter, sans-serif" fontWeight="600" fontSize="38" fill="#0f172a">Teqvis</text>
                    <rect x="180" y="47" width="8" height="8" fill="#0ea5e9" />
                  </svg>
                </div>
                <CardTitle className="text-2xl font-bold text-gray-900">3. Tech Reliability</CardTitle>
                <CardDescription>Dependable, Secure, Smart</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-center space-x-2 text-sm">
                  <div className="flex items-center space-x-1">
                    <div className="w-4 h-4 rounded-full bg-[#2563eb]"></div>
                    <span>Blue</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <div className="w-4 h-4 rounded-full bg-[#0ea5e9]"></div>
                    <span>Cyan</span>
                  </div>
                </div>
                <p className="text-gray-600 text-sm text-center">
                  A stylized shield with a subtle checkmark implies security and correctness. The cyan accent adds a "fresh" tech vibe.
                </p>
              </CardContent>
            </Card>
          </motion.div>

        </div>
        
        <div className="mt-16 text-center">
            <p className="text-gray-500 mb-6">Which one fits your vision best?</p>
            <Button onClick={() => window.history.back()} variant="outline" size="lg">
                <ArrowRight className="mr-2 rotate-180" size={16} /> Back to Home
            </Button>
        </div>
      </div>
    </div>
  );
};

export default LogoConcepts;
